<template lang="pug">
.study-list
  //- サイドメニュー部
  side-menu.study-list__side-menu(
    :currentLink='currentLink',
    :linkSettings='linkSettings',
    :linkEvent='sideMenuLinkEvent',
    :logoutEvent='logoutEvent'
  )
    template(#roleName)
      slot(name='roleName')
    template(#userUidTitle)
      slot(name='userUidTitle')
    template(#userNameSuffix)
      slot(name='userNameSuffix')
    template(#logoutText)
      slot(name='logoutText')
  //- コンテンツ表示部
  .study-list__content
    //- お知らせ表示部
    notification.study-list__content__notification(
      :notifications='notifications',
      :notificationEvent='notificationEvent',
      :initialDisplayLimit='notificationInitialLimit',
      v-if='notifications.length > 0'
    )
      template(#title)
        slot(name='notificationTitle')
    //- 画面タイトル
    text-frame.study-list__content__title.text-large.align-center
      slot(name='studyListTitle')
    //- 検索
    search-study.study-list__content__search(
      :searchEvent='searchEvent',
      :gradeSelectOptions='gradeSelectOptions',
      :yearSelectOptions='yearSelectOptions',
      :monthSelectOptions='monthSelectOptions',
      :gradeValue='gradeValue',
      :yearValue='yearValue',
      :monthValue='monthValue',
      :gradeChangeEvent='setGradeValue',
      :yearChangeEvent='setYearValue',
      :monthChangeEvent='setMonthValue'
    )
      template(#gradeLabel)
        slot(name='searchGradeLabel')
      template(#yearLabel)
        slot(name='searchYearLabel')
      template(#monthLabel)
        slot(name='searchMonthLabel')
      template(#searchButtonText)
        slot(name='searchButtonText')
    //- 解き方説明
    .study-list__content__explanation
      .study-list__content__explanation__text.text-xlarge.font-medium
        slot(name='explanationText')
      button-primary.study-list__content__explanation__button(@click='showMovieEvent')
        slot(name='showMovieButtonText')
      button-primary.study-list__content__explanation__button(v-if='showMoveSampleButton', @click='moveSampleEvent')
        slot(name='moveSampleButtonText')
    //- 一覧表示
    study-list-table.study-list__content__list(
      v-if='studyList',
      :studyList='studyList',
      :buttonEvent='listButtonEvent'
    )
      template(#subjectHeader)
        slot(name='subjectHeader')
      template(#sUnitHeader)
        slot(name='sUnitHeader')
      template(#lastMinuteCheckHeader)
        slot(name='lastMinuteCheckHeader')
      template(#resolveHeader)
        slot(name='resolveHeader')
      template(#learnedLabel)
        slot(name='learnedLabel')
      template(#notLearnedLabel)
        slot(name='notLearnedLabel')
  //- モーダル部
  modal-notification.study-list__modal(:notification='notification', ref='modalNotification')
  modal-show-movie.study-list__modal(:movieUrl='movieUrl', ref='modalShowMovie')
    template(#hideText)
      slot(name='hideMovieText')
</template>

<script lang="ts">
import { Component, Prop, Vue, Ref, Watch } from 'vue-property-decorator'
import SideMenu from '@/components/organisms/student/SideMenu.vue'
import Notification from '@/components/organisms/student/Notification.vue'
import TextFrame from '@/components/atoms/TextFrame.vue'
import SearchStudy from '@/components/organisms/student/SearchStudy.vue'
import ButtonPrimary from '@/components/molecules/student/ButtonPrimary.vue'
import StudyListTable from '@/components/organisms/student/StudyListTable.vue'
import ModalNotification from '@/components/organisms/student/ModalNotification.vue'
import ModalShowMovie from '@/components/organisms/student/ModalShowMovie.vue'
import { LinkSetting } from '@/const/SideMenuSettings'
import { InputItem } from '@/models/inputItem'
import { StudyItemListType } from '@/models/studyListModel'
import { NotificationItem } from '@/models/notificationModel'

/**
 * テスト一覧画面テンプレート
 */
@Component({
  components: {
    SideMenu,
    Notification,
    TextFrame,
    SearchStudy,
    ButtonPrimary,
    StudyListTable,
    ModalNotification,
    ModalShowMovie,
  },
})
export default class StudyList extends Vue {
  // モーダルに表示するお知らせ
  private notification: NotificationItem | null = null

  // このページのリンク設定
  @Prop()
  currentLink!: LinkSetting
  // サイドメニューリンク設定
  @Prop()
  linkSettings!: LinkSetting[]
  // サイドメニューリンク押下時のイベント
  @Prop()
  sideMenuLinkEvent!: Function
  // ログアウトボタン押下時のイベント
  @Prop()
  logoutEvent!: Function
  // お知らせ一覧
  @Prop()
  notifications!: NotificationItem[]
  // お知らせの初期表示上限
  @Prop()
  notificationInitialLimit!: number
  // 検索ボタン押下時イベント
  @Prop()
  searchEvent!: Function
  // 学年選択オプション
  @Prop()
  gradeSelectOptions!: InputItem[]
  // 実施年度選択オプション
  @Prop()
  yearSelectOptions!: InputItem[]
  // 実施月選択オプション
  @Prop()
  monthSelectOptions!: InputItem[]
  // サンプル問題ボタン押下時イベント
  @Prop()
  moveSampleEvent!: Function
  // テスト一覧表示情報
  @Prop()
  studyList?: StudyItemListType[]
  // 一覧内のボタン押下時イベント
  @Prop()
  listButtonEvent!: Function
  // 解き方動画URL
  @Prop()
  movieUrl!: string
  // サンプル問題ボタン表示フラグ
  @Prop()
  showMoveSampleButton!: boolean

  // モーダルのRef
  @Ref() modalNotification!: ModalNotification
  @Ref() modalShowMovie!: ModalShowMovie

  // === 選択値を保持する変数群
  // 学年
  public gradeValue = ''
  // 実施年度
  public yearValue = ''
  // 実施月
  public monthValue = ''

  // 実施年度が変更された際呼び出し元にイベントを送信
  @Watch('yearValue')
  private yearUpdated() {
    this.$emit('yearUpdated')
  }

  // === 選択値をセットする関数群
  // 学年をセット
  private setGradeValue(value: string) {
    this.gradeValue = value
  }
  // 実施年度をセット
  private setYearValue(value: string) {
    this.yearValue = value
  }
  // 実施月をセット
  private setMonthValue(value: string) {
    this.monthValue = value
  }

  // お知らせリンク押下時イベント
  private async notificationEvent(notification: NotificationItem) {
    // 選択されたお知らせ情報をセット
    this.notification = notification
    // 本文はbase64で渡されるため、デコードに成功した場合はその値を使用する
    const message = await Vue.prototype.$compress.base64ToString(notification.message)
    if (message !== null) this.notification.message = message
    // お知らせモーダルを開く
    this.modalNotification.show()
  }

  // 動画を見るボタン押下時イベント
  private showMovieEvent() {
    // 解き方動画表示モーダルを開く
    this.modalShowMovie.show()
  }
}
</script>

<style lang="stylus" scoped>
.study-list
  display flex
  margin 0 20px
  padding 20px 0
  width 100%
  height 100%

  &__side-menu
    margin-right 20px

  &__content
    width calc(100% - 300px)

    &__notification
      margin-bottom 40px

    &__title
      width 100%
      padding 20px
      background-color var(--color-default__box-background)
      margin-bottom 10px

    &__search
      margin-bottom 20px

    &__explanation
      display flex
      align-items center
      padding 10px
      margin-bottom 20px

      &__button
        width 180px
        margin-left 10px

    &__list
      margin-bottom 10px
</style>
